import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GitBranch, Activity, Sparkles } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import FlowsPage from "./FlowsPage";
import FlowInsightsPage from "./FlowInsightsPage";
import FlowMarketplacePage from "./FlowMarketplacePage";

/**
 * Single landing page that bundles the three flow surfaces under one roof.
 * Tab is reflected in the URL via ?tab=builder|insights|marketplace so refresh + deep links
 * keep working. Legacy routes (/flow-insights, /flow-marketplace) still load the page
 * with the right tab pre-selected.
 */
const TAB_FROM_PATH = {
  "/flows": "builder",
  "/flow-insights": "insights",
  "/flow-marketplace": "marketplace",
};

export default function FlowsHubPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const initial = params.get("tab") || TAB_FROM_PATH[location.pathname] || "builder";
  const [tab, setTab] = useState(initial);

  // keep state in sync when route changes (e.g. clicking the sidebar entry again)
  useEffect(() => {
    const fromPath = TAB_FROM_PATH[location.pathname];
    if (fromPath && fromPath !== tab) setTab(fromPath);
  }, [location.pathname]); // eslint-disable-line

  const onTabChange = (next) => {
    setTab(next);
    // canonicalise to /flows?tab=… so refresh keeps the tab
    navigate(`/flows?tab=${next}`, { replace: true });
  };

  return (
    <div className="flex flex-col h-full" data-testid="flows-hub">
      <div className="px-6 pt-5 pb-2 border-b bg-white dark:bg-slate-900">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Sparkles className="text-[#8B1A1A]" size={22} /> Flow Builder
        </h1>
        <p className="text-xs text-slate-500 mt-0.5">
          Design automations, watch them run, and install ready-made ones — all in one place.
        </p>
        <Tabs value={tab} onValueChange={onTabChange} className="mt-3">
          <TabsList className="bg-slate-100 dark:bg-slate-800">
            <TabsTrigger value="builder" data-testid="hub-tab-builder" className="data-[state=active]:bg-white data-[state=active]:text-[#8B1A1A]">
              <GitBranch size={14} className="mr-1.5" /> Builder
            </TabsTrigger>
            <TabsTrigger value="insights" data-testid="hub-tab-insights" className="data-[state=active]:bg-white data-[state=active]:text-[#8B1A1A]">
              <Activity size={14} className="mr-1.5" /> Insights
            </TabsTrigger>
            <TabsTrigger value="marketplace" data-testid="hub-tab-marketplace" className="data-[state=active]:bg-white data-[state=active]:text-[#8B1A1A]">
              <Sparkles size={14} className="mr-1.5" /> Marketplace
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="flex-1 overflow-auto">
        {tab === "builder" && <FlowsPage />}
        {tab === "insights" && <FlowInsightsPage />}
        {tab === "marketplace" && <FlowMarketplacePage />}
      </div>
    </div>
  );
}
